import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Instagram,
  Tag,
  ArrowRight,
  Loader2,
  Globe2,
  Check,
  Wallet,
  ShieldCheck,
  Handshake,
  FileText,
  ChevronDown,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '@/store';
import { userService } from '@/services/user.service';
import { fetchCurrentUser } from '@/store/slices/authSlice';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';

const DISMISS_KEY = 'creator_profile_gate_dismissed_at';
const OPPS_AGREED_KEY = 'creator_open_to_opportunities_at';
/** Show the nudge again after this many ms (3 days). */
const NUDGE_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;

function hasAgreedToOpportunities(user: any): boolean {
  if (user?.openToOpportunities) return true;
  if (user?.openToOpportunitiesAt) return true;
  return !!localStorage.getItem(OPPS_AGREED_KEY);
}

/** Returns true when the overlay should show for this session. */
function isAdminSession(): boolean {
  try {
    const admin = JSON.parse(localStorage.getItem('adminUser') || '{}');
    return !!admin?.id;
  } catch {
    return false;
  }
}

type GateUser = {
  accountType?: string;
  socialLinks?: { instagram?: string };
  location?: string;
  brand?: string;
  brand_name?: string;
  openToOpportunities?: boolean;
  openToOpportunitiesAt?: string;
} | null;

/** True when there's genuinely something to nudge this designer about,
 *  ignoring the snooze. Shared by the timing logic below. */
function baseGateEligible(user: GateUser): boolean {
  if (!user) return false;
  // Never show to admin users.
  if (isAdminSession()) return false;
  // Designers only: skip buyers and any unknown / unset account types.
  if (user.accountType !== 'creator' && user.accountType !== 'individual') return false;

  const profileIncomplete =
    !user.socialLinks?.instagram ||
    !user.location ||
    !(user.brand_name || user.brand);

  const oppsPending = !hasAgreedToOpportunities(user);

  return profileIncomplete || oppsPending;
}

/** Whether the "remind me later" snooze is currently active. */
function isSnoozed(): boolean {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;
  return Date.now() - parseInt(dismissedAt, 10) < NUDGE_INTERVAL_MS;
}

export function shouldShowProfileGate(user: GateUser): boolean {
  if (!baseGateEligible(user)) return false;
  // Respect the "remind me later" snooze.
  if (isSnoozed()) return false;
  return true;
}

/** Hold the (non-urgent) nudge for this long after login so we don't ambush
 *  the designer the moment they land. */
export const PROFILE_GATE_DELAY_MS = 4 * 60 * 1000; // 4 minutes

// Per-login-session flag: caps the gate to a single appearance per login.
// sessionStorage survives reloads/navigation within the tab but is cleared on
// logout (see `resetGateSession`), so the next login gets a fresh nudge.
const SESSION_SHOWN_KEY = 'creator_profile_gate_shown_session';
export const wasGateShownThisSession = (): boolean =>
  sessionStorage.getItem(SESSION_SHOWN_KEY) === '1';
export const markGateShownThisSession = (): void => {
  try { sessionStorage.setItem(SESSION_SHOWN_KEY, '1'); } catch { /* noop */ }
};
export const resetGateSession = (): void => {
  try { sessionStorage.removeItem(SESSION_SHOWN_KEY); } catch { /* noop */ }
};

/**
 * When (and whether) to surface the profile gate:
 *  - 'immediate' — brand name is missing. The marketplace filters on it, so
 *                  prompt right away and IGNORE the "remind me later" snooze.
 *  - 'delayed'   — only softer nudges remain (agreement, Instagram, location);
 *                  hold `PROFILE_GATE_DELAY_MS` and respect the snooze.
 *  - 'none'      — nothing to nudge, or (softer nudges only) currently snoozed.
 */
export type ProfileGateTiming = 'immediate' | 'delayed' | 'none';

export function getProfileGateTiming(user: GateUser): ProfileGateTiming {
  if (!baseGateEligible(user)) return 'none';

  // Brand name is required by the marketplace — always prompt, even if the
  // designer previously hit "remind me later".
  const brandMissing = !(user?.brand_name || user?.brand);
  if (brandMissing) return 'immediate';

  // Everything else is optional-ish: honour the snooze.
  if (isSnoozed()) return 'none';
  return 'delayed';
}

interface Props {
  onDone: () => void;
}

// Service fees, stored in NGN (single source of truth). Displayed in the
// user's selected currency via useCurrencyFormat.
const FEE_TIER_THRESHOLD_NGN = 300_000;
const FEE_SMALL_FLAT_NGN = 1_000;
const FEE_LARGE_FLAT_NGN = 25_000;

const CreatorProfileGate: React.FC<Props> = ({ onDone }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const { format: fmt, currency } = useCurrencyFormat();

  // Skip step 1 if the user has already agreed in a previous session.
  const [step, setStep] = useState<1 | 2>(hasAgreedToOpportunities(user) ? 2 : 1);
  const [agreed, setAgreed] = useState(false);
  const [agreeing, setAgreeing] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const [instagram, setInstagram] = useState(user?.socialLinks?.instagram || '');
  const [location, setLocation] = useState(user?.location || '');
  const [brandName, setBrandName] = useState(user?.brand_name || user?.brand || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filled = instagram.trim() && location.trim() && brandName.trim();

  const handleAgree = async () => {
    if (!agreed || agreeing) return;
    setAgreeing(true);
    setError('');
    const nowIso = new Date().toISOString();
    // Local stamp so the gate never asks again on this device, even if API doesn't persist the field.
    localStorage.setItem(OPPS_AGREED_KEY, nowIso);
    try {
      await userService.updateProfile({
        openToOpportunities: true,
        openToOpportunitiesAt: nowIso,
      });
      await dispatch(fetchCurrentUser());
    } catch {
      // Non-blocking — local stamp still keeps the gate from re-asking.
    } finally {
      setAgreeing(false);
      setStep(2);
    }
  };

  const handleSave = async () => {
    if (!filled || saving) return;
    setSaving(true);
    setError('');
    try {
      await userService.updateProfile({
        brand_name: brandName.trim(),
        location: location.trim(),
        socialLinks: { instagram: instagram.trim() },
      });
      // Refresh Redux user so the gate recalculates everywhere.
      await dispatch(fetchCurrentUser());
      // Clear any snooze stamp so the gate doesn't reappear.
      localStorage.removeItem(DISMISS_KEY);
      onDone();
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLater = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    onDone();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="profile-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[200] bg-white flex flex-col"
      >
        {/* Top accent strip */}
        <div className="h-1 bg-gray-900 w-full flex-shrink-0" />

        <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-xl"
          >
            {/* Stepper */}
            <div className="flex items-center gap-2 mb-7">
              {[1, 2].map((n) => {
                const active = step === n;
                const complete = step > n;
                return (
                  <React.Fragment key={n}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                          complete || active
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {complete ? <Check size={12} strokeWidth={3} /> : n}
                      </div>
                      <span
                        className={`text-[12px] font-semibold ${
                          active || complete ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {n === 1 ? 'Agreement' : 'Your profile'}
                      </span>
                    </div>
                    {n === 1 && (
                      <div className="flex-1 h-px bg-gray-200 relative overflow-hidden mx-1">
                        <motion.div
                          initial={false}
                          animate={{ width: step > 1 ? '100%' : '0%' }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                          className="absolute inset-y-0 left-0 bg-gray-900"
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.div
                  key="step-1"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="mb-5">
                    <div className="w-11 h-11 rounded-2xl bg-gray-900 flex items-center justify-center mb-5">
                      <Globe2 size={20} className="text-white" />
                    </div>
                    <h1 className="text-[22px] font-bold text-gray-900 leading-tight">
                      Get paid to make what you do best
                    </h1>
                    <p className="text-[14px] text-gray-500 mt-2 leading-relaxed">
                      Opt in and fashion lovers worldwide can discover your profile on Creators Archive by Ankaragrid Africa, send custom dress requests, and pay you upfront. No free work. No chasing invoices.
                    </p>
                  </div>

                  {/* Three-line summary — always visible, no pressure */}
                  <ul className="space-y-2 mb-3">
                    <li className="flex items-start gap-2.5 text-[13px] text-gray-700">
                      <Wallet size={15} className="text-gray-900 mt-0.5 flex-shrink-0" />
                      <span><strong className="text-gray-900">Money lands first.</strong> You get paid before you cut a single fabric or start production.</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-[13px] text-gray-700">
                      <Handshake size={15} className="text-gray-900 mt-0.5 flex-shrink-0" />
                      <span>You set your price, your timeline, and your design rules. Creators Archive by Ankaragrid Africa brings the fashion lovers.</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-[13px] text-gray-700">
                      <ShieldCheck size={15} className="text-gray-900 mt-0.5 flex-shrink-0" />
                      <span>Only pay when you earn. <span className="text-gray-500">Service fee starts at 10% + {fmt(FEE_SMALL_FLAT_NGN)}.</span></span>
                    </li>
                  </ul>

                  {/* Preview terms toggle */}
                  <button
                    type="button"
                    onClick={() => setShowTerms((v) => !v)}
                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border border-gray-200 hover:border-gray-400 transition-colors mb-4"
                  >
                    <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                      <FileText size={14} className="text-gray-700" />
                      Preview terms & fees
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-gray-500 transition-transform ${showTerms ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {showTerms && (
                      <motion.div
                        key="terms-detail"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 mb-4 space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Handshake size={14} className="text-gray-900" />
                              <h3 className="text-[12px] font-bold text-gray-900 uppercase tracking-wide">How it works</h3>
                            </div>
                            <p className="text-[12.5px] text-gray-600 leading-relaxed">
                              Fashion lovers browse your profile on Creators Archive by Ankaragrid Africa, send custom dress requests, and you decide if the job is right. You set your rate, your timeline, and your design scope. We handle the connection and the payment. You focus on the craft.
                            </p>
                          </div>

                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Wallet size={14} className="text-gray-900" />
                              <h3 className="text-[12px] font-bold text-gray-900 uppercase tracking-wide">Our cut, plainly</h3>
                            </div>
                            <p className="text-[12.5px] text-gray-600 leading-relaxed mb-2">
                              Creators Archive by Ankaragrid Africa only earns when you do. The service fee is deducted automatically when a fashion lover pays:
                            </p>
                            <div className="rounded-xl bg-white border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                              <div className="px-3 py-2.5 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[12px] font-semibold text-gray-900">Orders under {fmt(FEE_TIER_THRESHOLD_NGN)}</p>
                                  <p className="text-[11px] text-gray-500">Smaller projects and one-offs</p>
                                </div>
                                <p className="text-[12px] font-bold text-gray-900 whitespace-nowrap">10% + {fmt(FEE_SMALL_FLAT_NGN)}</p>
                              </div>
                              <div className="px-3 py-2.5 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[12px] font-semibold text-gray-900">Orders {fmt(FEE_TIER_THRESHOLD_NGN)} and above</p>
                                  <p className="text-[11px] text-gray-500">Flat top-up. You keep more on big jobs</p>
                                </div>
                                <p className="text-[12px] font-bold text-gray-900 whitespace-nowrap">10% + {fmt(FEE_LARGE_FLAT_NGN)}</p>
                              </div>
                            </div>
                            <p className="text-[11px] text-gray-400 leading-relaxed mt-2">
                              No listing fee. No subscription. No charge for declined briefs. Payouts settle in NGN.{currency !== 'NGN' ? ' Amounts above are converted at the live rate.' : ''}
                            </p>
                          </div>

                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <ShieldCheck size={14} className="text-gray-900" />
                              <h3 className="text-[12px] font-bold text-gray-900 uppercase tracking-wide">You stay in control</h3>
                            </div>
                            <ul className="text-[12.5px] text-gray-600 leading-relaxed space-y-1 list-disc pl-4 marker:text-gray-400">
                              <li>Accept, decline, or ignore dress requests. No penalties.</li>
                              <li>You get paid upfront before production starts. No chasing invoices.</li>
                              <li>You own your designs. Fashion lovers receive a license, not the rights.</li>
                              <li>Opt out any time in Account Settings. Your profile leaves Creators Archive by Ankaragrid Africa search instantly.</li>
                            </ul>
                          </div>

                          <p className="text-[11px] text-gray-500 leading-relaxed pt-3 border-t border-gray-200">
                            Full details in Creators Archive by Ankaragrid Africa{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-gray-900 font-medium">Terms of Service</a>{' '}
                            and{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-gray-900 font-medium">Privacy Policy</a>.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    type="button"
                    onClick={() => setAgreed((v) => !v)}
                    className="w-full flex items-start gap-3 text-left mb-5 group"
                  >
                    <span
                      className={`mt-0.5 w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center transition-all ${
                        agreed
                          ? 'bg-gray-900 border-gray-900'
                          : 'bg-white border-2 border-gray-300 group-hover:border-gray-500'
                      }`}
                    >
                      {agreed && <Check size={13} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className="text-[13px] text-gray-700 leading-snug">
                      I have read and agree to the terms and fee schedule. Make my profile visible to fashion lovers on Creators Archive by Ankaragrid Africa.
                    </span>
                  </button>

                  {error && (
                    <p className="text-[13px] text-rose-600 mb-3">{error}</p>
                  )}

                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={handleAgree}
                      disabled={!agreed || agreeing}
                      className="w-full py-3.5 rounded-full bg-gray-900 hover:bg-black text-white text-[14px] font-bold transition-colors disabled:bg-gray-200 disabled:text-gray-400 inline-flex items-center justify-center gap-2"
                    >
                      {agreeing ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>Agree and continue <ArrowRight size={15} /></>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="w-full py-3 text-[13px] text-gray-500 hover:text-gray-800 font-medium transition-colors"
                    >
                      Skip for now
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="step-2"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Header */}
                  <div className="mb-6">
                    <div className="w-11 h-11 rounded-2xl bg-gray-900 flex items-center justify-center mb-5">
                      <Tag size={20} className="text-white" />
                    </div>
                    <h1 className="text-[22px] font-bold text-gray-900 leading-tight">
                      Look professional. Get chosen.
                    </h1>
                    <p className="text-[14px] text-gray-500 mt-2 leading-relaxed">
                      Fashion lovers on Creators Archive by Ankaragrid Africa filter by location, brand, and Instagram. Designers with all three get up to <span className="text-gray-900 font-semibold">5× more dress requests</span>.
                    </p>
                  </div>

                  {/* Why it matters */}
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-100 mb-5">
                    <div className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-bold">✓</div>
                    <div>
                      <p className="text-[12px] font-semibold text-emerald-900 leading-snug">
                        Get discovered by fashion lovers
                      </p>
                      <p className="text-[11px] text-emerald-700 leading-snug mt-0.5">
                        Complete these 3 fields and your profile goes live on Creators Archive by Ankaragrid Africa.
                      </p>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-[12px] font-semibold text-gray-700 block mb-1.5">Brand name</label>
                      <div className="relative">
                        <Tag size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="e.g. Kemi Atelier"
                          value={brandName}
                          onChange={e => setBrandName(e.target.value)}
                          className="w-full pl-9 pr-4 py-3 rounded-2xl bg-gray-50 border border-gray-200 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-900 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[12px] font-semibold text-gray-700 block mb-1.5">Instagram URL</label>
                      <div className="relative">
                        <Instagram size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="url"
                          placeholder="https://instagram.com/yourbrand"
                          value={instagram}
                          onChange={e => setInstagram(e.target.value)}
                          className="w-full pl-9 pr-4 py-3 rounded-2xl bg-gray-50 border border-gray-200 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-900 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[12px] font-semibold text-gray-700 block mb-1.5">Location</label>
                      <div className="relative">
                        <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Lagos, Nigeria"
                          value={location}
                          onChange={e => setLocation(e.target.value)}
                          className="w-full pl-9 pr-4 py-3 rounded-2xl bg-gray-50 border border-gray-200 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-900 transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {error && (
                    <p className="text-[13px] text-rose-600 mt-3">{error}</p>
                  )}

                  <div className="mt-7 space-y-3">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={!filled || saving}
                      className="w-full py-3.5 rounded-full bg-gray-900 hover:bg-black text-white text-[14px] font-bold transition-colors disabled:bg-gray-200 disabled:text-gray-400 inline-flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>Save and continue <ArrowRight size={15} /></>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleLater}
                      className="w-full py-3 text-[13px] text-gray-500 hover:text-gray-800 font-medium transition-colors"
                    >
                      Remind me later
                    </button>
                  </div>

                  <p className="text-[11px] text-gray-400 text-center mt-5 leading-relaxed">
                    You can update these any time in Account Settings.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreatorProfileGate;
